/**
 * RectSelectionHandler
 * RECT Entity 타입의 선택 핸들러 로직
 */

import type { IPath } from '../../../backend/IRenderer'
import type { SelectionStrategy } from '../../base/selectionStrategy'
import type { BaseSelectionHandler } from '../../base/baseSelectionHandler'
import { CANVAS_ENTITY_EVENT } from '../../../const'

export class RectSelectionHandler implements SelectionStrategy {
  /**
   * RECT 타입 선택 핸들러 추가
   * @param entity - BaseSelectionHandler 인스턴스
   */
  addHandlers(entity: BaseSelectionHandler): void {
    const systemLayer = entity.canvasEngine.getRenderer().layers.system
    if (!systemLayer) {
      throw new Error('시스템 레이어가 없습니다.')
    }

    const shape = entity.getShape()
    if (!shape) {
      throw new Error('도형이 없습니다.')
    }

    // Transformer 초기화
    entity.destroyTransformer()
    const target = shape

    const interactionEnabled =
      entity.getEntityInteractionMode() && entity.isSelected && !entity.canvasEngine.isPinchZooming

    // 드래그 가능하게 설정
    target.draggable(interactionEnabled)

    // (원본의 hitStrokeWidth(parseInt(hitStrokeWidth())) 정규화는 사각 shape가 rectPad 20을 가져 no-op이라 삭제)

    // Transformer 생성 (RECT는 회전/리사이즈 모두 활성화)
    const shapeTransformer = entity.createSelectionHandles(
      {
        id: 'single-selection',
        padding: 20,
        anchorCornerRadius: 20,
        anchorSize: 20,
        rotateEnabled: interactionEnabled,
        resizeEnabled: interactionEnabled,
        borderStrokeWidth: 2,
        ignoreStroke: true,
        flipEnabled: false,
        keepRatio: false,
        draggable: interactionEnabled,
        anchorDraggable: interactionEnabled,
        anchorHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
        borderHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
      },
      [target],
    )
    entity.setShapeTransformer(shapeTransformer)
    entity.canvasEngine.initShapeTransformer(shapeTransformer)
    shapeTransformer.attach([shape])

    // Transform 이벤트 (실시간 위치 업데이트)
    shapeTransformer.on('transform', () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치 중이면 transform 중단
        shapeTransformer.stopTransform()
        return
      }
      entity.removeShapeAnimation()
      entity.hideButtonGroup()
    })

    // Transform End 이벤트
    const eventManager = entity.getEventManager()
    eventManager.removeEvent(shape, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END)
    eventManager.addEvent(shape, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치로 중단된 transform의 잔존 scale을 흡수해 화면-데이터 정합 유지
        // (저장/UI 복원은 스킵 — 이후 조작 시 저장됨. 0 스케일은 transformShape가 무시.
        //  ELLIPSE도 이 핸들러를 공유하므로 타입 분기가 내장된 transformShape를 그대로 호출)
        entity.transformShape()
        return
      }

      if (!entity.isRect()) {
        entity.transformShape()
      } else {
        // RECT의 경우 cloud border 크기 변경 감지
        const rectEntity = entity as BaseSelectionHandler & {
          __cloudBorder?: IPath
        }
        if (rectEntity.__cloudBorder) {
          entity.updateCloudBorderIfSizeChanged()
        }
      }

      entity.updateBalloonPositionPublic()
      entity.canvasEngine.events.onEntityTransform(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
      entity.addShapeAnimation()
      entity.canvasEngine.restoreAllEntitiyListening()
    })

    // Transformed 이벤트
    eventManager.removeEvent(shape, CANVAS_ENTITY_EVENT.SELECT_TRANSFORMED)
    eventManager.addEvent(shape, CANVAS_ENTITY_EVENT.SELECT_TRANSFORMED, () => {
      if (!entity.isSelected) return
      // 멀티터치 진입 프레임에서도 scale 흡수는 수행 — 스킵하면 잔존 scale이 저장 시
      // 누락되어 크기가 유실됨 (0 스케일 프레임은 transformShape가 무시)
      if (entity.isRect()) entity.transformShape()
      if (entity.canvasEngine.isPinchZooming) return
      entity.canvasEngine.disableAnotherEntityListening(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })

    // Drag Start 이벤트
    eventManager.removeEvent(shape, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
    eventManager.addEvent(shape, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치 중이면 드래그 중단
        if (shape.isDragging()) {
          shape.stopDrag()
        }
        return
      }
      if (entity.balloon) {
        if (!entity.getRippleFollowMode()) {
          entity.removeShapeAnimation()
        }
      }
    })

    // Drag Move 이벤트
    eventManager.removeEvent(shape, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
    eventManager.addEvent(shape, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return
      if (!shape) return

      // Shape의 드래그된 위치를 Entity 위치로 실시간 반영
      const x = shape.x()
      const y = shape.y()
      entity.setShapePosition(0, 0)
      entity.setEntityPosition(entity.node.x() + x, entity.node.y() + y)

      // 파동 애니메이션 따라다니기 모드
      if (entity.balloon && entity.getRippleFollowMode()) {
        entity.updateRipplePosition()
      }

      entity.hideButtonGroup()
    })

    // Drag End 이벤트
    eventManager.removeEvent(shape, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
    eventManager.addEvent(shape, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return
      if (!shape) return

      const x = shape.x()
      const y = shape.y()
      entity.setShapePosition(0, 0)
      entity.setEntityPosition(entity.node.x() + x, entity.node.y() + y)

      // 파동 애니메이션 복원
      if (entity.balloon && !entity.getRippleFollowMode()) {
        entity.addShapeAnimation()
      }

      entity.updateBalloonPositionPublic()
      entity.canvasEngine.events.onEntityTransform(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })

    // 시스템 레이어에 Transformer 추가
    systemLayer.add(shapeTransformer.node)
    systemLayer.batchDraw()
  }
}
